import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Building2,
  CheckCircle2,
  XCircle,
  Home,
  DoorOpen,
  DoorClosed,
  HelpCircle,
  X,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;

const getStatusMeta = (occupancyRate) => {
  if (occupancyRate >= 80) {
    return {
      label: "High",
      className: "bg-red-100 text-red-700 hover:bg-red-200",
    };
  }
  if (occupancyRate >= 50) {
    return {
      label: "Medium",
      className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
    };
  }
  return {
    label: "Low",
    className: "bg-green-100 text-green-700 hover:bg-green-200",
  };
};

function Availability() {
  const [openAvailabilityGuide, setOpenAvailabilityGuide] = useState(false);

  const getAccommodations = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");

    const res = await fetch(`${API_URL}/api/sdaowner/get_accommodations`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.message || data.msg || "Failed to fetch accommodations"
      );
    }

    return (data && data.accommodations) || [];
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["sdaowner-accommodations"],
    queryFn: getAccommodations,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (prev) => prev, // prevents blank/flicker on refetch
  });

  const accommodations = data || [];

  // Aggregate accommodations by suburb/location
  const availabilityData = useMemo(() => {
    const map = {}; // { [suburb]: { suburb, totalProperties, vacant, occupied } }

    accommodations.forEach((acc) => {
      // determine suburb/location
      const suburb =
        acc.suburb ||
        acc.location ||
        (acc.address && (acc.address.suburb || acc.address.city)) ||
        "Unknown";

      if (!map[suburb]) {
        map[suburb] = { suburb, totalProperties: 0, vacant: 0, occupied: 0 };
      }

      map[suburb].totalProperties += 1;

      // heuristics to decide if this accommodation is vacant or occupied
      const status = (acc.status || "").toString().toLowerCase();
      const hasCurrentTenant =
        acc.currentTenant || acc.tenant || acc.occupiedBy || acc.renter;
      const hasBookings =
        Array.isArray(acc.bookings) && acc.bookings.length > 0;

      const isAvailableFlag =
        // explicit boolean flags
        (typeof acc.isAvailable === "boolean" && acc.isAvailable) ||
        (typeof acc.isVacant === "boolean" && acc.isVacant) ||
        // common field names
        acc.available === true ||
        // textual status
        /vacant|available|free|unoccupied/i.test(status);

      const isOccupiedFlag =
        (typeof acc.isOccupied === "boolean" && acc.isOccupied) ||
        acc.occupied === true ||
        /occupied|rented|booked|taken|leased|tenanted/i.test(status) ||
        !!hasCurrentTenant ||
        !!hasBookings;

      // If both flags detected, prefer occupied (bookings/tenant presence is stronger)
      if (isOccupiedFlag) {
        map[suburb].occupied += 1;
      } else if (isAvailableFlag) {
        map[suburb].vacant += 1;
      } else {
        // fallback: if nothing clear, assume occupied if there's a current tenant or booking,
        // otherwise treat as vacant (you can change this behavior if your API has definitive flags)
        if (hasCurrentTenant || hasBookings) {
          map[suburb].occupied += 1;
        } else {
          map[suburb].vacant += 1;
        }
      }
    });

    // convert map to array and compute occupancy rate if needed later
    return Object.values(map);
  }, [accommodations]);

  // Totals
  const totalProperties = availabilityData.reduce(
    (sum, item) => sum + item.totalProperties,
    0
  );
  const totalVacant = availabilityData.reduce(
    (sum, item) => sum + item.vacant,
    0
  );
  const totalOccupied = availabilityData.reduce(
    (sum, item) => sum + item.occupied,
    0
  );

  // Protect against division by 0
  const occupancyPercent =
    totalProperties > 0
      ? Math.round((totalOccupied / totalProperties) * 100)
      : 0;
  const occupancyProgressValue =
    totalProperties > 0 ? (totalOccupied / totalProperties) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Properties */}
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Properties</CardTitle>
            <Building2 className="h-4 w-4 text-[#D2138C]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900">{totalProperties}</div>
            <p className="text-xs text-gray-500 mt-1">Across all locations</p>
          </CardContent>
        </Card>

        {/* Vacant Properties */}
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Vacant Properties</CardTitle>
            <DoorOpen className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-green-600">{totalVacant}</div>
            <p className="text-xs text-gray-500 mt-1">Available for booking</p>
          </CardContent>
        </Card>

        {/* Occupied Properties */}
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Occupied Properties</CardTitle>
            <DoorClosed className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-red-600">{totalOccupied}</div>
            <p className="text-xs text-gray-500 mt-1">Currently rented</p>
          </CardContent>
        </Card>

        {/* Occupancy Rate */}
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Occupancy Rate</CardTitle>
            <Home className="h-4 w-4 text-[#D2138C]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900">{occupancyPercent}%</div>
            <Progress
              value={occupancyProgressValue}
              className="mt-2 h-2"
              indicatorColor="bg-[#D2138C]"
            />
          </CardContent>
        </Card>
      </div>

      {/* Availability by suburb */}
      <Card className="shadow-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Availability by location</CardTitle>

            {isFetching && !isLoading && (
              <div className="text-xs text-gray-400">Updating...</div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpenAvailabilityGuide(true)}
            className="h-10 w-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
            aria-label="Availability guide"
            title="Availability guide">
            <HelpCircle className="h-5 w-5 text-[#D2138C]" />
          </button>
        </CardHeader>
        <CardContent>
          {/* ✅ Mobile view (stacked cards) */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              <div className="text-center py-6 text-gray-500">
                Loading availability...
              </div>
            ) : error ? (
              <div className="text-center py-6 text-red-600">
                Failed to load availability.
              </div>
            ) : availabilityData.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No accommodations found.
              </div>
            ) : (
              availabilityData.map((item, index) => {
                const occupancyRate =
                  item.totalProperties > 0
                    ? (item.occupied / item.totalProperties) * 100
                    : 0;

                const status = getStatusMeta(occupancyRate);

                return (
                  <Card key={index} className="rounded-xl border shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">
                            {item.suburb}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Total properties: {item.totalProperties}
                          </div>
                        </div>

                        <Badge
                          variants="Secondary"
                          className={status.className}>
                          {status.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="rounded-lg border p-3">
                          <div className="text-xs text-gray-500 mb-1">
                            Vacant
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <div className="text-lg font-semibold text-green-700">
                              {item.vacant}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border p-3">
                          <div className="text-xs text-gray-500 mb-1">
                            Occupied
                          </div>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <div className="text-lg font-semibold text-red-700">
                              {item.occupied}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            Occupancy rate
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {Math.round(occupancyRate)}%
                          </div>
                        </div>

                        <Progress
                          value={occupancyRate}
                          className="mt-2 h-2"
                          indicatorColor="bg-[#D2138C]"
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* ✅ Desktop view (table) */}
          <div className="hidden md:block rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-black">Location</TableHead>
                  <TableHead className="text-black">Total</TableHead>
                  <TableHead className="text-black">Vacant</TableHead>
                  <TableHead className="text-black">Occupied</TableHead>
                  <TableHead className="text-black">Occupancy Rate</TableHead>
                  <TableHead className="text-black">Status</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-gray-500">
                      Loading availability...
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-red-600">
                      Failed to load availability.
                    </TableCell>
                  </TableRow>
                ) : availabilityData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-gray-500">
                      No accommodations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  availabilityData.map((item, index) => {
                    const occupancyRate =
                      item.totalProperties > 0
                        ? (item.occupied / item.totalProperties) * 100
                        : 0;

                    const status = getStatusMeta(occupancyRate);

                    return (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="text-gray-900">
                          {item.suburb}
                        </TableCell>

                        <TableCell className="text-gray-900">
                          {item.totalProperties}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-green-600">
                              {item.vacant}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-red-600">
                              {item.occupied}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-900">
                              {Math.round(occupancyRate)}%
                            </div>
                            <Progress
                              value={occupancyRate}
                              className="h-2 w-24"
                              indicatorColor="bg-[#D2138C]"
                            />
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge
                            variants="Secondary"
                            className={status.className}>
                            {status.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      {openAvailabilityGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenAvailabilityGuide(false)}
            aria-label="Close guide"
          />

          {/* Modal */}
          <div className="relative z-10 w-[95%] max-w-xl rounded-2xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Availability guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenAvailabilityGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
              <p>
                This section shows a summary of your properties per location,
                based on the current property status and bookings.
              </p>

              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <span className="font-medium">Total</span>: total properties
                  in that location
                </li>
                <li>
                  <span className="font-medium">Vacant</span>: available for
                  booking
                </li>
                <li>
                  <span className="font-medium">Occupied</span>: currently
                  rented / active booking
                </li>
                <li>
                  <span className="font-medium">Occupancy rate</span>: occupied
                  ÷ total
                </li>
                <li>
                  <span className="font-medium">Status</span>: quick indicator
                  of availability (Low/Medium/High)
                </li>
              </ul>

              <div className="text-xs text-gray-500">
                Tip: To change availability, update the property status in
                Manage Accommodations or create/update a booking.
              </div>
            </div>

            <div className="border-t px-5 py-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOpenAvailabilityGuide(false)}
                className="rounded-xl bg-[#D2138C] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Availability;
